import { App as CapApp } from "@capacitor/app";
import type { StytchClient } from "@stytch/react";

import { AuthProvider } from "@/enums/auth-provider";
import type { AuthenticationClient } from "@/lib/auth/authentication-client";
import {
  clearStytchSessionTokens,
  getStytchSessionJwt,
  getStytchSessionTokens,
  SESSION_HYDRATION_DURATION_MINUTES,
  setStytchSessionTokens,
} from "@/lib/auth/stytch-session-storage";
import { isInvalidSessionError } from "@/lib/mappers/stytch-error";
import { HASURA_STYTCH_ENDPOINT } from "@/lib/utils/config";
import { logError } from "@/lib/utils/log-error";

const HASURA_JWT_CLAIMS = "https://hasura.io/jwt/claims";
const X_HASURA_DEFAULT_ROLE = "x-hasura-default-role";
const HASURA_USER_ROLE = "user";

/** Refresh the JWT when it expires within this many minutes (the Stytch JWT lives ~5 min). */
const TOKEN_REFRESH_BUFFER_MINUTES = 1;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

export class StytchAuthenticationClient implements AuthenticationClient {
  private _hydrationReady: Promise<boolean> = Promise.resolve(false);
  private _refreshInFlight: Promise<string | null> | null = null;

  constructor(private readonly stytchClient: StytchClient) {
    this.setupSessionPersistence();
  }

  /**
   * Performs async initialization: hydrates the session from persisted tokens and
   * registers the app-state-change listener for re-hydration on foreground.
   * Must be called once after construction and awaited before the instance is used.
   */
  async initialize(): Promise<void> {
    this._hydrationReady = this.hydrateSession();
    await this._hydrationReady;
    this.hydrateSessionOnAppStateChangeToActive();
  }

  /** Resolves when the initial (or post-foreground) session hydration attempt completes. */
  get hydrationReady(): Promise<boolean> {
    return this._hydrationReady;
  }

  get activeProvider(): AuthProvider {
    return AuthProvider.Stytch;
  }

  get hasuraEndpoint(): string {
    return HASURA_STYTCH_ENDPOINT;
  }

  /** Returns true if a Stytch session is currently loaded in memory. */
  isSessionActive(): boolean {
    return (
      this.stytchClient.session.getSync() !== null &&
      this.stytchClient.session.getTokens() !== null
    );
  }

  isAuthenticated(): Promise<boolean> {
    return Promise.resolve(this.isSessionActive());
  }

  async hasValidServerSession(): Promise<boolean> {
    if (!this.isSessionActive()) {
      return false;
    }

    try {
      await this.authenticateAndPersistSession();

      return true;
    } catch (error) {
      if (isInvalidSessionError(error)) {
        await this.logout();

        return false;
      }

      // Transient failure (network unreachable, 5xx, rate limit): the session
      // already passed isSessionActive(), so trust the in-memory session rather
      // than revoking a possibly-valid one. A genuinely invalid session is caught
      // again by the OIDC grant downstream and by the next hydration cycle.
      return true;
    }
  }

  async getTokenSilently(): Promise<string> {
    const tokens = this.stytchClient.session.getTokens();
    const jwt = tokens?.session_jwt ?? (await getStytchSessionJwt());

    if (!jwt) {
      return "";
    }

    // While the cached JWT is comfortably valid, use it as-is.
    if (
      !StytchAuthenticationClient.isJwtExpiringWithin(jwt, TOKEN_REFRESH_BUFFER_MINUTES)
    ) {
      return jwt;
    }

    return (await this.refreshSessionJwt()) ?? jwt;
  }

  /**
   * Mints a fresh session JWT, deduplicating concurrent callers into a single
   * in-flight refresh so that queries resuming together (UserQuery +
   * RecipientQuery + …) do not stampede session.authenticate().
   */
  private async refreshSessionJwt(): Promise<string | null> {
    this._refreshInFlight ??= this.performSessionRefresh();

    try {
      return await this._refreshInFlight;
    } finally {
      this._refreshInFlight = null;
    }
  }

  /**
   * Performs the actual refresh.
   * - Returns the new JWT on success.
   * - Returns "" when the session is invalid (401), after logging the user out.
   * - Returns null on a transient failure so the caller keeps the cached JWT.
   */
  private async performSessionRefresh(): Promise<string | null> {
    // Stored-only path: no in-memory session to authenticate against.
    // hydrateSession primes the session (updateSession) and then authenticates.
    if (!this.isSessionActive()) {
      const hydrated = await this.hydrateSession();

      return hydrated
        ? (this.stytchClient.session.getTokens()?.session_jwt ?? null)
        : null;
    }

    try {
      return await this.authenticateAndPersistSession();
    } catch (error) {
      this.logAuthError(error, "getTokenSilently");

      if (isInvalidSessionError(error)) {
        await this.logout();

        return "";
      }

      return null;
    }
  }

  /**
   * Authenticates the session against Stytch — rotating the JWT — and persists
   * the fresh tokens. Error handling is deliberately left to each caller: the
   * flows differ on 401 cleanup (logout vs. clearing stored tokens) and on the
   * fallback semantics for transient failures.
   */
  private async authenticateAndPersistSession(): Promise<string> {
    const result = await this.stytchClient.session.authenticate({
      session_duration_minutes: SESSION_HYDRATION_DURATION_MINUTES,
    });

    await setStytchSessionTokens(result.session_token, result.session_jwt);

    return result.session_jwt;
  }

  /** Reports an auth failure to Sentry, tagged with the Stytch provider and originating flow. */
  private logAuthError(
    error: unknown,
    flow: "getTokenSilently" | "hydrateSession",
  ): void {
    logError(error, {
      tags: {
        "auth.provider": "stytch",
        "auth.flow": flow,
      },
    });
  }

  getUserSub(): Promise<string> {
    return Promise.resolve(this.stytchClient.session.getSync()?.user_id ?? "");
  }

  /**
   * Validates the Hasura default role against the custom claims stored in the active session.
   * Reads from the in-memory session object — no JWT decoding required.
   */
  hasValidRole(): Promise<boolean> {
    const session = this.stytchClient.session.getSync();

    if (!session) {
      return Promise.resolve(false);
    }

    const claims = session.custom_claims;

    if (!isRecord(claims)) {
      return Promise.resolve(false);
    }

    const hasuraClaims = claims[HASURA_JWT_CLAIMS];

    if (!isRecord(hasuraClaims)) {
      return Promise.resolve(false);
    }

    return Promise.resolve(hasuraClaims[X_HASURA_DEFAULT_ROLE] === HASURA_USER_ROLE);
  }

  async logout(): Promise<void> {
    // Clear persisted tokens FIRST so that even if the app restarts mid-revoke
    // (e.g. due to Auth0's redirect), hydrateSession() won't find anything to restore.
    await clearStytchSessionTokens();

    try {
      await this.stytchClient.session.revoke();
    } catch {
      // Server revoke is best-effort; local tokens are already cleared.
    }
  }

  /**
   * Registers a listener that persists session tokens to capacitor preferences on every
   * session change (including JWT rotation). Clears stored tokens on logout.
   * Must be called once at app startup, outside the React tree.
   */
  setupSessionPersistence(): void {
    this.stytchClient.session.onChange((session) => {
      if (!session) {
        return;
      }

      const tokens = this.stytchClient.session.getTokens();

      if (!tokens) {
        return;
      }

      void setStytchSessionTokens(tokens.session_token, tokens.session_jwt);
    });
  }

  /**
   * Restores a Stytch session from tokens persisted in capacitor preferences.
   * On iOS, cookies are deleted by the OS between app launches; this keeps users
   * authenticated without requiring re-login.
   *
   * - Calls updateSession + authenticate, extending the session by 30 days, when tokens are found.
   * - Clears stored tokens and returns false if the session has expired.
   */

  async hydrateSession(): Promise<boolean> {
    const tokensFromCookies = this.stytchClient.session.getTokens();
    const { sessionToken, sessionJwt } =
      (tokensFromCookies
        ? {
            sessionToken: tokensFromCookies.session_token,
            sessionJwt: tokensFromCookies.session_jwt,
          }
        : await getStytchSessionTokens()) ?? {};

    const hydrated = await this.hydrateSessionWithTokens(
      sessionToken ?? "",
      sessionJwt ?? "",
    );

    return hydrated;
  }

  async hydrateSessionWithTokens(
    sessionToken: string,
    sessionJwt: string,
  ): Promise<boolean> {
    if (!sessionToken || !sessionJwt) {
      return false;
    }

    try {
      this.stytchClient.session.updateSession({
        session_token: sessionToken,
        session_jwt: sessionJwt,
      });

      await this.authenticateAndPersistSession();

      return true;
    } catch (error) {
      this.logAuthError(error, "hydrateSession");

      if (isInvalidSessionError(error)) {
        await clearStytchSessionTokens();
      }

      return false;
    }
  }

  private hydrateSessionOnAppStateChangeToActive(): void {
    void CapApp.addListener("appStateChange", ({ isActive }) => {
      const tokens = this.stytchClient.session.getTokens();

      if (
        isActive &&
        (!tokens?.session_jwt ||
          StytchAuthenticationClient.isJwtExpiringWithin(
            tokens.session_jwt,
            TOKEN_REFRESH_BUFFER_MINUTES,
          ))
      ) {
        this._hydrationReady = this.hydrateSession();
      }
    });
  }

  /**
   * Returns true if the JWT expires within the given number of minutes.
   * Returns true on any parse error so the caller defaults to re-hydrating.
   */
  private static isJwtExpiringWithin(jwt: string, minutes: number): boolean {
    try {
      const payload = jwt.split(".")[1];
      const decoded = JSON.parse(atob(payload)) as { exp?: number };

      if (!decoded.exp) {
        return true;
      }

      return Date.now() + minutes * 60 * 1000 >= decoded.exp * 1000;
    } catch {
      return true;
    }
  }
}
